import React, { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";

const Contact = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    timezone: ''
  });

  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');

  type LocationsType = {
    "Europe": string[];
    "Middle East": string[];
    "North America": string[];
    "Asia Pacific": string[];
    "Africa": string[];
    "South America": string[];
  };

  const locations: LocationsType = {
    "Europe": [
      "Paris (UTC+1)",
      "London (UTC+0)",
      "Berlin (UTC+1)",
      "Madrid (UTC+1)",
      "Rome (UTC+1)",
      "Amsterdam (UTC+1)",
      "Brussels (UTC+1)",
      "Vienna (UTC+1)",
      "Stockholm (UTC+1)",
      "Zurich (UTC+1)"
    ],
    "Middle East": [
      "Dubai (UTC+4)",
      "Riyadh (UTC+3)",
      "Doha (UTC+3)",
      "Manama (UTC+3)",
      "Kuwait City (UTC+3)",
      "Muscat (UTC+4)"
    ],
    "North America": [
      "New York (UTC-5)",
      "Los Angeles (UTC-8)",
      "Chicago (UTC-6)",
      "Toronto (UTC-5)",
      "Vancouver (UTC-8)",
      "Montreal (UTC-5)"
    ],
    "Asia Pacific": [
      "Singapore (UTC+8)",
      "Tokyo (UTC+9)",
      "Hong Kong (UTC+8)",
      "Seoul (UTC+9)",
      "Sydney (UTC+11)",
      "Melbourne (UTC+11)"
    ],
    "Africa": [
      "Cairo (UTC+2)",
      "Johannesburg (UTC+2)",
      "Casablanca (UTC+1)",
      "Lagos (UTC+1)",
      "Nairobi (UTC+3)"
    ],
    "South America": [
      "São Paulo (UTC-3)",
      "Buenos Aires (UTC-3)",
      "Bogota (UTC-5)",
      "Lima (UTC-5)",
      "Santiago (UTC-4)"
    ]
  };

  const filteredLocations = Object.entries(locations).reduce<LocationsType>((acc, [region, cities]) => {
    const filteredCities = cities.filter(city => 
      city.toLowerCase().includes(locationSearch.toLowerCase())
    );
    if (filteredCities.length > 0) {
      acc[region as keyof LocationsType] = filteredCities;
    }
    return acc;
  }, {} as LocationsType);

  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'error';
    messages: string[];
  }>({
    show: false,
    type: 'success',
    messages: []
  });

  // Function to show alert
  const showAlert = (type: 'success' | 'error', messages: string[]) => {
    setAlert({
      show: true,
      type,
      messages
    });
    // Auto hide after 5 seconds
    setTimeout(() => {
      setAlert(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation checks
    const errors: string[] = [];
    
    // Required fields check
    if (!formData.firstName.trim()) {
      errors.push(language === "en" ? "First name is required" : "Le prénom est requis");
    }
    if (!formData.lastName.trim()) {
      errors.push(language === "en" ? "Last name is required" : "Le nom est requis");
    }
    if (!formData.email && !formData.phone) {
      errors.push(language === "en" 
        ? "Please provide either an email address or phone number" 
        : "Veuillez fournir une adresse email ou un numéro de téléphone");
    }
    
    // Email format validation if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push(language === "en" ? "Invalid email format" : "Format d'email invalide");
    }
    
    // Phone format validation if provided (basic check for numbers and common separators)
    if (formData.phone && !/^[0-9+\-() ]*$/.test(formData.phone)) {
      errors.push(language === "en" ? "Invalid phone number format" : "Format de numéro de téléphone invalide");
    }

    // If there are validation errors, show them and stop
    if (errors.length > 0) {
      showAlert('error', errors);
      return;
    }

    try {
      // Send data to webhook
      const response = await fetch('https://n8n.systemx.tech/webhook/ab7b9a62-b792-470a-b512-508e88ab0c1a', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          timestamp: new Date().toISOString(),
          source: 'website_contact_form'
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      // Clear form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        timezone: ''
      });

      // Show success message
      showAlert('success', [
        language === "en" 
          ? "Thank you for your message! We'll get back to you soon."
          : "Merci pour votre message ! Nous vous répondrons bientôt."
      ]);

    } catch (error) {
      console.error('Error submitting form:', error);
      showAlert('error', [
        language === "en"
          ? "Sorry, there was an error sending your message. Please try again later."
          : "Désolé, une erreur s'est produite lors de l'envoi de votre message. Veuillez réessayer plus tard."
      ]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const inputClassName = `
    w-full 
    p-3 
    rounded-lg 
    border-2 
    focus:outline-none 
    focus:ring-2 
    transition-all
    ${isDark 
      ? "bg-black border-green-500/30 focus:border-green-400 focus:ring-green-400/20 text-white placeholder:text-gray-500" 
      : "bg-white border-green-900/30 focus:border-green-800 focus:ring-green-800/20 text-gray-900 placeholder:text-gray-400"
    }
  `;

  const labelClassName = `block mb-2 font-medium ${
    isDark ? "text-gray-200" : "text-gray-700"
  }`;

  const RequiredStar = () => (
    <span className="text-red-500 ml-1">*</span>
  );

  return (
    <section 
      id="contact"
      className="relative py-32 px-8 overflow-hidden">
      {/* Alert Component */}
      {alert.show && (
        <div className={`
          fixed top-24 left-1/2 transform -translate-x-1/2 z-50
          min-w-[300px] max-w-md
          p-4 rounded-lg shadow-lg
          border-2
          transition-all duration-300 ease-in-out
          ${alert.type === 'success' 
            ? isDark 
              ? 'bg-green-900/90 border-green-500 text-white' 
              : 'bg-green-50 border-green-500 text-green-900'
            : isDark
              ? 'bg-red-900/90 border-red-500 text-white'
              : 'bg-red-50 border-red-500 text-red-900'
          }
        `}>
          <div className="flex flex-col space-y-2">
            {alert.messages.map((message, index) => (
              <p key={index} className="text-sm">
                {message}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-20">
          <h2 className={`
            text-4xl md:text-5xl lg:text-6xl 
            font-bold 
            ${isDark 
              ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
              : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
            }
            bg-clip-text 
            text-transparent
            mb-6
            animate-gradient-x
          `}
          style={{ lineHeight: '1.2', paddingBottom: '10px' }}>
            {language === "en" ? "Contact Us" : "Contactez-Nous"}
          </h2>
          <div className={`w-24 h-1 mx-auto rounded-full mt-6 ${
            isDark 
              ? "bg-gradient-to-r from-green-500/50 to-green-300/50" 
              : "bg-gradient-to-r from-green-900/50 to-green-700/50"
          }`} />

          {/* Notice about form purpose */}
          <p className={`mt-8 text-lg max-w-2xl mx-auto ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            {language === "en" 
              ? "This form is for general inquiries only. Ready to start your project? Click \"Get Started\" in the navigation bar to begin the discovery process."
              : "Ce formulaire est uniquement pour les questions générales. Prêt à démarrer votre projet ? Cliquez sur \"Commencer\" dans la barre de navigation pour débuter le processus de découverte."}
          </p>
        </div>

        {/* Updated Contact Form */}
        <div className={`
          max-w-2xl 
          mx-auto 
          p-8 
          rounded-2xl 
          border-2
          ${isDark 
            ? "bg-black/50 border-green-500/30" 
            : "bg-white/50 border-green-900/30"
          }
        `}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClassName}>
                  {language === "en" ? "First Name" : "Prénom"}
                  <RequiredStar />
                </label>
                <input
                  type="text"
                  name="firstName"
                  required
                  placeholder={language === "en" ? "Your first name" : "Votre prénom"}
                  className={inputClassName}
                  onChange={handleChange}
                  value={formData.firstName}
                />
              </div>
              <div>
                <label className={labelClassName}>
                  {language === "en" ? "Last Name" : "Nom"}
                  <RequiredStar />
                </label>
                <input
                  type="text"
                  name="lastName"
                  required
                  placeholder={language === "en" ? "Your last name" : "Votre nom"}
                  className={inputClassName}
                  onChange={handleChange}
                  value={formData.lastName}
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClassName}>
                  {language === "en" ? "Email" : "Courriel"}
                  <RequiredStar />
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder={language === "en" ? "Your email address" : "Votre adresse email"}
                  className={inputClassName}
                  onChange={handleChange}
                  value={formData.email}
                />
              </div>
              <div>
                <label className={labelClassName}>
                  {language === "en" ? "Phone" : "Téléphone"}
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder={language === "en" ? "Your phone number" : "Votre numéro de téléphone"}
                  className={inputClassName}
                  onChange={handleChange}
                  value={formData.phone}
                />
              </div>
            </div>

            {/* Location/Timezone */}
            <div className="relative">
              <label className={labelClassName}>
                {language === "en" ? "Your Location" : "Votre Localisation"}
                <RequiredStar />
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="timezone"
                  required
                  placeholder={language === "en" ? "Search or enter your location" : "Rechercher ou entrer votre localisation"}
                  className={`${inputClassName} ${isLocationDropdownOpen ? 'rounded-b-none' : ''}`}
                  onChange={(e) => {
                    setLocationSearch(e.target.value);
                    setFormData(prev => ({ ...prev, timezone: e.target.value }));
                  }}
                  onFocus={() => setIsLocationDropdownOpen(true)}
                  value={formData.timezone}
                />
                {isLocationDropdownOpen && (
                  <div 
                    className={`
                      absolute 
                      z-10 
                      w-full 
                      max-h-60 
                      overflow-auto 
                      rounded-b-lg 
                      border-2
                      border-t-0
                      ${isDark 
                        ? "bg-black border-green-500/30" 
                        : "bg-white border-green-900/30"
                      }
                    `}
                  >
                    {Object.entries(filteredLocations).map(([region, cities]) => (
                      <div key={region}>
                        <div className={`
                          px-3 
                          py-2 
                          font-semibold
                          ${isDark ? "text-green-400" : "text-green-900"}
                          ${isDark ? "bg-black" : "bg-gray-50"}
                        `}>
                          {language === "en" ? region : region === "Europe" ? "Europe" 
                            : region === "Middle East" ? "Moyen-Orient"
                            : region === "North America" ? "Amérique du Nord"
                            : region === "Asia Pacific" ? "Asie Pacifique"
                            : region === "Africa" ? "Afrique"
                            : "Amérique du Sud"}
                        </div>
                        {cities.map((city) => (
                          <div
                            key={city}
                            className={`
                              px-4 
                              py-2 
                              cursor-pointer
                              ${isDark 
                                ? "hover:bg-green-500/10 text-gray-300" 
                                : "hover:bg-green-900/10 text-gray-700"
                              }
                            `}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, timezone: city }));
                              setLocationSearch(city);
                              setIsLocationDropdownOpen(false);
                            }}
                          >
                            {city}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Company */}
            <div>
              <label className={labelClassName}>
                {language === "en" ? "Company / Website" : "Entreprise / Site Web"}
                <RequiredStar />
              </label>
              <input
                type="text"
                name="company"
                required
                placeholder={language === "en" ? "Your company name or website" : "Nom de votre entreprise ou site web"}
                className={inputClassName}
                onChange={handleChange}
                value={formData.company}
              />
            </div>

            {/* Message */}
            <div>
              <label className={labelClassName}>
                {language === "en" ? "Message" : "Message"}
                <RequiredStar />
              </label>
              <textarea
                name="message"
                required
                rows={6}
                placeholder={
                  language === "en" 
                    ? "Tell us about your project and how we can help transform your business with AI solutions" 
                    : "Parlez-nous de votre projet et comment nous pouvons vous aider à transformer votre entreprise avec des solutions d'IA"
                }
                className={inputClassName}
                onChange={handleChange}
                value={formData.message}
              />
            </div>

            <button
              type="submit"
              className={`
                w-full 
                py-3 
                px-6 
                rounded-lg 
                font-medium 
                transition-all 
                duration-300
                ${isDark 
                  ? "bg-green-500 hover:bg-green-400 text-black" 
                  : "bg-green-900 hover:bg-green-800 text-white"
                }
              `}
            >
              {language === "en" ? "Send Message" : "Envoyer"}
            </button>
          </form>
        </div>
      </div>

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
        <div className={`absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
      </div>
    </section>
  );
};

export default Contact;